/* eslint-disable max-lines */
/* eslint-disable react/boolean-prop-naming */
/* eslint-disable react/jsx-handler-names */
"use client";
import { useEffect, useMemo, useState } from "react";
import dynamic from "next/dynamic";
import { useDispatch, useSelector } from "react-redux";
import {
  clearPersonlizedImageStore,
  setMultiImageConfig,
  setSuccessfullyAddedToast,
  setUserPersonalization,
} from "../../../../store/slices/personalizationSlice";
import PropTypes from "prop-types";
import {
  checkMultiUploadedImageCount,
  handlePersonalizationDetailsOnRefresh,
  handleSetUserPersonalizations,
  imageCountConfig,
} from "../../../../utils/pdp/personalization";
import ButtonComponent from "../../../atoms/button";
import Drawer from "../../drawer";
import Typography from "../../../atoms/typography";
import Toast from "../../../atoms/toast";
import { constants, UPLOAD_PHOTOS } from "../../../../constants/pageConstants/pdpConstants";
import IconButtonComponent from "../../../atoms/iconButton";
import ImageAndTextTab from "../multiImageWithTextPersonalization/imageAndTextTab";
import { removeLocalStorageItem } from "../../../../utils/storage/localStorageUtil";
import { unlockBody } from "../../../../utils/document";
import useAnalytics from "../../../../analytics/useAnalytics";
import { cleverTapCommonEvents } from "../../../../analytics/clevertapConstants";
import { closeBottomDrawerFromParent } from "../../../../utils/common";
import { COUNTRIES } from "../../../../constants/common/common";

const MultiImageUploadInput = dynamic(() => import("./multiImageUploadInput"));
const FileUploadInstructions = dynamic(() => import("./fileUploadInstructions"));

/**
 * MultiImagePersonalization component for managing image uploads in product personalization.
 *
 * @param {Object} props - The properties passed to the component.
 * @param {Object} props.item - Contains the configuration details such as allowed media types, size constraints, and field metadata for the image upload.
 * @param {string} props.productId - The unique identifier for the product associated with the personalization.
 * @param {function} props.setPreviewImage - Function to set or update the preview image upon successful image upload.
 * @param {string} props.previewImage - The currently displayed preview image URL.
 * @param {boolean} props.isMobile - Flag indicating if the component is rendered in a mobile view.
 * @param {boolean} props.imageWithText - Flag to indicate if the personalization includes both images and text.
 * @param {Array} props.personalizedOptions - Array of personalization options for the product.
 * @param {function} props.setTextEntered - Function to set the text entered status.
 *
 * @returns {JSX.Element} The rendered MultiImagePersonalization component.
 */
const MultiImagePersonalization = ({
  item,
  productId,
  setPreviewImage,
  previewImage,
  isMobile,
  imageWithText,
  personalizedOptions,
  setTextEntered,
  cleverTapData,
}) => {
  const { maxInputRequired, minInputRequired, fieldName, contentType, label } = item;
  const dispatch = useDispatch();
  const { trackEvent } = useAnalytics();
  const [currentToast, setCurrentToast] = useState({});
  const [isToastVisible, setIsToastVisible] = useState(null);
  const [multiImageDrawer, setMultiImageDrawer] = useState(false);
  const [allImageUploadValidation, setAllImageUploadValidation] = useState(false);

  const multiImageConfig = useSelector((state) => state.personalization.multiImageConfig);
  const uploadFinalStatus = useSelector((state) => state.personalization?.uploadFinalStatus);
  const imageUploadError = useSelector((state) => state.personalization?.imageUploadError);
  const uploadProgressStatus = useSelector((state) => state.personalization?.uploadProgressStatus);
  const selectedPincode = useSelector((state) => state.locationLock.selectedDomesticLocation?.pincode);
  const userPersonalization = useSelector((state) => state.personalization.userPersonalization);
  const multiImageText = useSelector((state) => state.personalization.multiImageText);
  const clearPersonalizedData = useSelector((state) => state.personalization.clearPersonalizedData);
  const catalogId = useSelector((state) => state?.appConfig?.catalogId);
  const selectedIntLocation = useSelector((state) => state?.locationLock.selectedIntLocation);

  const uploadedImageCount = checkMultiUploadedImageCount(multiImageConfig);
  const allImgUploaded = parseInt(minInputRequired, 10) === uploadedImageCount;
  const isUploadIncomplete = parseInt(minInputRequired, 10) > uploadedImageCount;
  const initialConfig = imageCountConfig(maxInputRequired);

  useEffect(() => {
    if (clearPersonalizedData) {
      dispatch(setMultiImageConfig(initialConfig));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [clearPersonalizedData]);

  useEffect(() => {
    // Fetch data from local storage on component load
    const personalizationFromLocalStore = handlePersonalizationDetailsOnRefresh(productId);
    if (personalizationFromLocalStore && Array.isArray(personalizationFromLocalStore)) {
      imageWithText && setTextEntered(true);
      dispatch(setUserPersonalization(personalizationFromLocalStore));
      // Find the specific entry for the current fieldName and set it as the text field value
      const matchingEntry = personalizationFromLocalStore?.find((entry) => entry.fieldName === fieldName);
      if (matchingEntry && matchingEntry.value?.[0]) {
        const configsMappedFromLocalStorage = initialConfig?.map((item, index) => {
          if (matchingEntry?.value[index]) {
            return {
              ...item,
              imgUrl: matchingEntry?.value[index],
              uploaded: true,
            };
          }
          return item;
        });

        dispatch(setMultiImageConfig(configsMappedFromLocalStorage));
      }
    } else {
      dispatch(setMultiImageConfig(initialConfig));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fieldName, productId]);

  useEffect(() => {
    if (allImgUploaded) {
      setPreviewImage(true);
      trackEvent(
        cleverTapCommonEvents.uploadPhotoDone,
        {
          ...cleverTapData,
          photo_count: uploadedImageCount,
          is_photo_uploaded: true,
        },
        ["clevertap"],
      );
    } else {
      setPreviewImage(false);
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [allImgUploaded]);

  /**
   * Sets validation error and shows toast when required images are not uploaded.
   */
  const handleUploadValidationError = () => {
    setAllImageUploadValidation(true);
    setIsToastVisible(true);
    setCurrentToast({});
  };

  /**
   * Saves personalization data to storage, handling different cases based on image and text configurations.
   */
  const savePersonalizationToStorage = () => {
    const personalizationValue = {
      fieldName,
      contentType,
      label,
      index: null,
      value: multiImageConfig?.map((image) => image?.imgUrl) || [],
    };

    const personalizationFromStore = imageWithText ? [...multiImageText] : userPersonalization;

    handleSetUserPersonalizations(productId, personalizationValue, personalizationFromStore, dispatch);
  };

  const checkValidationForText = useMemo(() => {
    const textContentType = personalizedOptions?.filter((item) => item.contentType === "TEXT");
    const filteredArray =
      Array.isArray(multiImageText) &&
      multiImageText.length > 0 &&
      multiImageText?.filter((item) => item !== null && item?.value.some((value) => value !== ""));

    return textContentType?.length === filteredArray?.length;
  }, [multiImageText, personalizedOptions]);

  /**
   * Proceeds to the preview step and closes the multi-image drawer.
   */
  const proceedToPreview = () => {
    if (imageWithText) {
      if (checkValidationForText) {
        setTextEntered(true);
        setMultiImageDrawer(false);
        setPreviewImage(true);
        dispatch(setSuccessfullyAddedToast(true));
        savePersonalizationToStorage();
      } else {
        const tab = document.getElementById("tab_2");
        if (tab) {
          tab.click(); // triggering the click event to switch to second tab
        }
      }
    } else {
      setPreviewImage(true);
      setMultiImageDrawer(false);
      savePersonalizationToStorage();
    }
  };

  /**
   * Handles the continuation of the multi-image upload process.
   */
  const onContinueMultiImageUpload = () => {
    if (isUploadIncomplete) {
      handleUploadValidationError();
      return;
    }

    proceedToPreview();
    unlockBody();
  };

  // Handle toast message updates based on state
  useEffect(() => {
    const toastMessages = {
      validationError: {
        variant: "info",
        header: constants.PLEASE_UPLOAD_ALL_PHOTOS_TO_CONTINUE,
      },
      sizeError: {
        variant: "error",
        header: "Upload failed",
        subHeader: constants.PLEASE_CHECK_THE_INSTRUCTION_AGAIN,
      },
      success: {
        variant: "success",
        header: constants.PHOTOS_UPLOADED_SUCCESSFULLY,
      },
      progress: {
        variant: "info",
        header: constants.UPLOADING,
        subHeader: constants.WINDOW_REFRESH_MESSAGE,
        fill: uploadProgressStatus?.percentComplete,
        strokeWidth: "10",
      },
    };

    let currentToast = null;

    switch (true) {
      case allImageUploadValidation:
        currentToast = toastMessages.validationError;
        break;
      case imageUploadError:
        currentToast = toastMessages.sizeError;
        break;
      case uploadFinalStatus:
        currentToast = toastMessages.success;
        break;
      case !!uploadProgressStatus:
        currentToast = toastMessages.progress;
        break;
      default:
        break;
    }

    if (currentToast) {
      setCurrentToast(currentToast);
    }
  }, [uploadProgressStatus, uploadFinalStatus, allImageUploadValidation, imageUploadError]);

  /**
   * Checks if the toast should be cleared based on the upload or validation status.
   * @returns {boolean} - True if any error or final status is present.
   */
  const shouldClearToast = () => uploadFinalStatus || allImageUploadValidation || imageUploadError;

  /**
   * Resets the toast state by clearing the current toast and hiding it.
   */
  const resetToastState = () => {
    setCurrentToast({});
    setIsToastVisible(false);
  };

  /**
   * Handles the closing of the toast message.
   */
  const handleToastClose = () => {
    if (shouldClearToast()) {
      resetToastState();
      dispatch(clearPersonlizedImageStore());
      setAllImageUploadValidation(false);
    }
  };

  /**
   * Handles the closing of the Drawer.
   */
  const onDrawerAndTapClose = () => {
    if (!(uploadProgressStatus?.percentComplete === "" && !uploadFinalStatus)) return;
    unlockBody();
    closeBottomDrawerFromParent();
    const shouldSavePersonalization = !isUploadIncomplete && (!imageWithText || checkValidationForText);

    if (shouldSavePersonalization) {
      savePersonalizationToStorage();
      imageWithText && setTextEntered(true);
    } else {
      dispatch(setUserPersonalization(multiImageText || []));
      imageWithText && setTextEntered(false);
      removeLocalStorageItem(productId);
    }
    const tab = document.getElementById("tab_1");
    if (tab) {
      tab.click(); // triggering the click event to switch to second tab
    }
    setMultiImageDrawer(false);
  };

  const commonTypographyProps = {
    Tag: "p",
    classes: "w-full flex items-center flex-shrink-0 font-600 text-16 text-fnp-500",
  };

  const mobileStyles = "px-[16px] py-[10px] sm:flex border-b border-gray-300 ";
  const desktopStyles = "h-[48px] px-[16px] py-[10px] justify-between border-b border-gray-200 bg-white shadow-sm";

  return (
    <>
      <ButtonComponent
        classes="text-base flex justify-center w-full"
        buttonVariant="customOutlined"
        label={
          imageWithText
            ? constants.UPLOAD_CUSTOMIZE_PHOTOS
            : previewImage
              ? constants.CHANGE_PHOTOS
              : UPLOAD_PHOTOS(maxInputRequired)
        }
        onClick={() => {
          setMultiImageDrawer(true);
        }}
        isRemoveExtraPadding={true}
      />
      <Drawer
        position={isMobile ? "bottom" : "right"}
        isOpen={(COUNTRIES.INDIA === catalogId ? selectedPincode : selectedIntLocation?.pincode) && multiImageDrawer}
        onDrawerClose={onDrawerAndTapClose}
        styles={`p-0 h-full ${isMobile ? "" : "min-w-[360px] max-w-[400px] xll:max-w-[532px] xl:max-w-[400px]"}`}
        isDrawerCustomStyles={isMobile ? false : true}
        isBackdropOff={uploadProgressStatus?.percentComplete === "" && !uploadFinalStatus}
      >
        {isMobile ? (
          <div className={`flex items-center justify-start ${mobileStyles}`}>
            <IconButtonComponent
              labelColor="w-full font-normal"
              classes="py-2"
              iconSrc="/icons/left-arrow.svg"
              onClick={onDrawerAndTapClose}
            />
            <Typography {...commonTypographyProps} classes={`${commonTypographyProps.classes} pl-12`}>
              {imageWithText ? constants.CUSTOMIZE_PRODUCT : constants.UPLOAD_PHOTOS}
            </Typography>
          </div>
        ) : (
          <Typography {...commonTypographyProps} classes={`${commonTypographyProps.classes} ${desktopStyles}`}>
            {imageWithText ? constants.CUSTOMIZE_PRODUCT : constants.UPLOAD_PHOTOS}
          </Typography>
        )}
        {imageWithText ? (
          <ImageAndTextTab
            item={item}
            productId={productId}
            setPreviewImage={setPreviewImage}
            isMobile={isMobile}
            imageWithText={imageWithText}
            personalizedOptions={personalizedOptions}
            setTextEntered={setTextEntered}
            isUploadIncomplete={isUploadIncomplete}
            setIsToastVisible={setIsToastVisible}
            setCurrentToast={setCurrentToast}
            setMultiImageDrawer={setMultiImageDrawer}
            onContinueMultiImageUpload={onContinueMultiImageUpload}
          />
        ) : (
          <>
            <div className="h-[calc(100vh-130px)] overflow-auto pb-20 pl-20  pr-20 pt-12 ">
              <MultiImageUploadInput
                item={item}
                productId={productId}
                setIsToastVisible={setIsToastVisible}
                setCurrentToast={setCurrentToast}
              />
              <FileUploadInstructions item={item} />
            </div>
            <div className="bg-white fixed bottom-0 flex w-full flex-col items-center gap-[10px] border-t border-gray-200 px-[20px] py-[16px]">
              <ButtonComponent
                classes="text-base flex justify-center w-[360px]"
                buttonVariant="filled"
                label="Continue"
                onClick={onContinueMultiImageUpload}
                isRemoveExtraPadding={true}
              />
            </div>
          </>
        )}
        {isToastVisible && (
          <div className="fixed bottom-100 left-1/2 z-20 flex w-full max-w-md -translate-x-1/2 transform items-center justify-center">
            <Toast
              variant={currentToast?.variant}
              isToast={isToastVisible}
              header={currentToast?.header}
              subHeader={currentToast?.subHeader}
              duration={3000}
              onToastChange={handleToastClose}
              fill={currentToast?.fill}
              strokeWidth={currentToast?.strokeWidth || "10"}
            />
          </div>
        )}
      </Drawer>
    </>
  );
};

MultiImagePersonalization.propTypes = {
  item: PropTypes.shape({
    allowedMediaType: PropTypes.string.isRequired,
    minSize: PropTypes.number.isRequired,
    minUnit: PropTypes.string.isRequired,
    maxSize: PropTypes.number.isRequired,
    maxUnit: PropTypes.string.isRequired,
    contentType: PropTypes.string.isRequired,
    fieldName: PropTypes.string.isRequired,
    label: PropTypes.string.isRequired,
    required: PropTypes.bool.isRequired,
    maxInputRequired: PropTypes.number.isRequired,
    minInputRequired: PropTypes.number.isRequired,
    value: PropTypes.arrayOf(
      PropTypes.shape({
        url: PropTypes.string.isRequired,
        id: PropTypes.number.isRequired,
      }),
    ).isRequired,
  }).isRequired,
  indexNumber: PropTypes.number,
  setPreviewImage: PropTypes.func.isRequired,
  setImageSizeError: PropTypes.func.isRequired,
  productId: PropTypes.string.isRequired,
  previewImage: PropTypes.bool,
  isMobile: PropTypes.bool,
  imageWithText: PropTypes.bool.isRequired,
  personalizedOptions: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.number.isRequired,
      text: PropTypes.string.isRequired,
    }),
  ).isRequired,
  setTextEntered: PropTypes.func.isRequired,
  cleverTapData: PropTypes.object,
};

export default MultiImagePersonalization;
